import { ObjectHelper } from "@swivl-mono/helpers";
import {
  AvailableMembersMap,
  NotificationBaseInterface,
  OnlineRosterEntry,
  SessionInterface,
  SocketEventCCAgentMessage,
  SocketEventCCAssignment,
  SocketEventCCConversationUpdate,
  SocketEventCCPresence,
  SocketEventCCVoiceEvent,
  SocketEventType,
  TicketPreview,
  UserInterface
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

//LINK - apps/notifications/src/API/Events/Events.Controller.ts
export class EventsAPI {
  static async broadcastAvailableMembers(orgId: number, availableMembersMap: AvailableMembersMap): Promise<boolean> {
    return APIService.Notifications.post<boolean>(`/events/org/${orgId}/available-members`, availableMembersMap);
  }
  static async broadcastSessionUpdate(orgId: number, session: Partial<SessionInterface>): Promise<boolean> {
    return APIService.Notifications.post<boolean>(`/events/org/${orgId}/type/session`, ObjectHelper.stripNulls(session));
  }
  static async broadcastEscalation(orgId: number, escalation: Partial<SessionInterface>): Promise<boolean> {
    return APIService.Notifications.post<boolean>(`/events/org/${orgId}/type/escalation`, ObjectHelper.stripNulls(escalation));
  }
  static async broadcastNotification(orgId: number, notification: NotificationBaseInterface): Promise<boolean> {
    return APIService.Notifications.post<boolean>(`/events/org/${orgId}/type/notification`, ObjectHelper.stripNulls(notification));
  }
  static async broadcastVars(userId: number, vars: { [key: string]: string | undefined }): Promise<boolean> {
    return APIService.Notifications.post<boolean>(`/events/org/${userId}/type/vars/user/${userId}`, vars);
  }
  static async broadcastUsersOnline(orgId: number, onlineUsers: { [userId: number]: OnlineRosterEntry }): Promise<boolean> {
    return APIService.Notifications.post<boolean>(`/events/org/${orgId}/type/users-online`, onlineUsers);
  }
  static async broadcastUserUpdated(orgId: number, userId: number, userData: Partial<UserInterface>): Promise<boolean> {
    return APIService.Notifications.post<boolean>(`/events/org/${orgId}/type/user/${userId}`, userData);
  }
  static async broadcastTicket(orgId: number, ticket: TicketPreview): Promise<boolean> {
    // ENG-3005 (2026-08-08): ticket events ride the bot socket's org room now
    // — same SocketEventType.TICKET + payload the studio always handled, new
    // transport, mirroring the Phase-4 CC migration. The notifications-server
    // route this used to hit is deleted (that server is being phased out).
    return APIService.Bot.post<boolean>(`/contact-center-relay/cc-broadcast`, {
      room: "org",
      targetId: orgId,
      event: SocketEventType.TICKET,
      payload: ticket
    });
  }

  // ─── Contact Center V3 Events ───────────────────────────────────────────
  // Realtime-unification Phase 4: Snowy CC events now flow over the bot socket's
  // rooms (org / member / conv), not the notifications server. These post to the
  // bot's `cc-broadcast` relay, which emits to watching agents. The event names
  // + payloads are unchanged, so the studio handlers are identical — only the
  // transport moved. Bot-originated emitters (presence, voice, conversation-list)
  // call `ChatSocket.broadcastCC` directly and never hit this hop.

  static async broadcastCCConversationUpdate(orgId: number, payload: SocketEventCCConversationUpdate["payload"]): Promise<boolean> {
    return APIService.Bot.post<boolean>(`/contact-center-relay/cc-broadcast`, {
      room: "org",
      targetId: orgId,
      event: SocketEventType.CC_CONVERSATION_UPDATE,
      payload
    });
  }

  static async broadcastCCAssignment(orgId: number, memberId: number, payload: SocketEventCCAssignment["payload"]): Promise<boolean> {
    return APIService.Bot.post<boolean>(`/contact-center-relay/cc-broadcast`, {
      room: "member",
      targetId: memberId,
      event: SocketEventType.CC_ASSIGNMENT,
      payload
    });
  }

  static async broadcastCCAgentMessage(orgId: number, payload: SocketEventCCAgentMessage["payload"]): Promise<boolean> {
    return APIService.Bot.post<boolean>(`/contact-center-relay/cc-broadcast`, {
      room: "org",
      targetId: orgId,
      event: SocketEventType.CC_AGENT_MESSAGE,
      payload
    });
  }

  static async broadcastCCPresence(orgId: number, payload: SocketEventCCPresence["payload"]): Promise<boolean> {
    return APIService.Bot.post<boolean>(`/contact-center-relay/cc-broadcast`, {
      room: "org",
      targetId: orgId,
      event: SocketEventType.CC_PRESENCE,
      payload
    });
  }

  static async broadcastCCVoiceEvent(orgId: number, payload: SocketEventCCVoiceEvent["payload"]): Promise<boolean> {
    return APIService.Bot.post<boolean>(`/contact-center-relay/cc-broadcast`, {
      room: "org",
      targetId: orgId,
      event: SocketEventType.CC_VOICE_EVENT,
      payload
    });
  }
}
